import { Space, Tag, message } from "antd";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import {
  getTournaments,
  createTournament,
  updateTournament,
  publishTournament,
  deleteTournament,
  getTournamentById,
  regenerateTournamentQuestions,
  regenerateTournamentQuestion,
  generateAdditionalQuestion,
  unpublishTournament,
} from "../apis/tournament";
import TournamentGenerateForm from "../components/tournament/TournamentGenerateForm";
import TournamentEditForm from "../components/tournament/TournamentEditForm";
import TournamentDetails from "../components/tournament/TournamentDetails";
import CommonTable from "../components/common/CommonTable";
import CutOffText from "../components/common/CutOffText";
import { useLoader } from "../components/common/Loader/LoaderProvider";
import ActionsModal from "../components/common/Modals/ActionsModal";
import TableAction from "../components/common/TableAction";
import CommonButton from "../components/common/Button/CommonButton";
import CustomLayout from "../components/layout/CustomLayout";
import withAuth from "../routeProtector/withAuth";
import useDocumentTitle from "../utils/useDocumentTitle";

const VIEW_LIST = "list";
const VIEW_CREATE = "create";
const VIEW_CREATE_MANUAL = "create_manual";
const VIEW_EDIT = "edit";
const VIEW_DETAIL = "detail";

const statusFilterOptions = [
  { value: "all", label: "All Status" },
  { value: "draft", label: "Draft" },
  { value: "upcoming", label: "Upcoming" },
  { value: "ongoing", label: "Ongoing" },
  { value: "completed", label: "Completed" },
];

const STATUS_LABELS = {
  draft: "Draft",
  upcoming: "Upcoming",
  ongoing: "Ongoing",
  completed: "Completed",
  published: "Published",
};

const lifecycleColor = (status) => {
  if (status === "ongoing") return "green";
  if (status === "upcoming") return "blue";
  if (status === "completed") return "default";
  return "orange";
};

const formatStatus = (status) => STATUS_LABELS[status] || status;
const DATETIME_FORMAT = "MMM DD, YYYY - hh:mm A";

const Tournaments = () => {
  const dispatch = useDispatch();
  const [tournaments, setTournaments] = useState([]);
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortedField, setSortedField] = useState("createdAt");

  const [currentView, setCurrentView] = useState(VIEW_LIST);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [deleteData, setDeleteData] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [publishModal, setPublishModal] = useState(false);
  const [publishData, setPublishData] = useState(null);
  const [unpublishModal, setUnpublishModal] = useState(false);
  const [unpublishData, setUnpublishData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [addingAiQuestion, setAddingAiQuestion] = useState(false);
  const [regeneratingQuestionId, setRegeneratingQuestionId] = useState(null);

  const { showLoader, hideLoader } = useLoader();
  useDocumentTitle("Tournaments");

  const debounceTimer = useRef(null);

  const handleSearchChange = useCallback((value) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(value);
      setCurrentPage(1);
    }, 500);
  }, []);

  const fetchTournaments = async () => {
    showLoader();
    try {
      const res = await dispatch(
        getTournaments({
          page: currentPage,
          limit,
          searchQuery: debouncedSearch.trim(),
          status: statusFilter,
          sortBy: sortedField,
          sortOrder,
        })
      );
      if (res.status) {
        const data = res.data?.data;
        setTournaments(data?.tournaments || []);
        setTotalItems(data?.totalTournaments || 0);
        setCurrentPage(data?.currentPage || 1);
      }
    } finally {
      hideLoader();
    }
  };

  const loadTournament = async (id) => {
    const res = await dispatch(getTournamentById(id));
    if (res.status) {
      setSelectedTournament(res.data?.data || null);
      return res.data?.data;
    }
    message.error(res.message || "Failed to load tournament");
    return null;
  };

  useEffect(() => {
    if (currentView === VIEW_LIST) {
      fetchTournaments();
    }
    // eslint-disable-next-line
  }, [
    limit,
    currentPage,
    debouncedSearch,
    sortedField,
    sortOrder,
    statusFilter,
    currentView,
  ]);

  const handleSort = (field) => {
    if (sortedField === field) {
      setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
    } else {
      setSortedField(field);
      setSortOrder("asc");
    }
  };

  const handleCreate = async (payload) => {
    setSaving(true);
    try {
      const res = await dispatch(createTournament(payload));
      if (res.status) {
        message.success(
          payload.questions
            ? "Tournament created successfully"
            : "Tournament created with AI-generated questions"
        );
        setSelectedTournament(res.data?.data || null);
        setCurrentView(VIEW_DETAIL);
      } else {
        message.error(res.message || "Failed to create tournament");
      }
    } catch (err) {
      message.error("Failed to create tournament");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAndPublish = async (payload) => {
    setPublishing(true);
    try {
      const createRes = await dispatch(createTournament(payload));
      if (!createRes.status) {
        message.error(createRes.message || "Failed to create tournament");
        return;
      }
      const newId = createRes.data?.data?._id;
      if (!newId) {
        message.warning("Tournament created but could not be published");
        setSelectedTournament(createRes.data?.data || null);
        setCurrentView(VIEW_DETAIL);
        return;
      }
      const pubRes = await dispatch(publishTournament(newId));
      if (pubRes.status) {
        message.success("Tournament created and published successfully");
        setSelectedTournament(null);
        setCurrentView(VIEW_LIST);
      } else {
        message.warning(
          "Created as draft but failed to publish: " + (pubRes.message || "")
        );
        setSelectedTournament(createRes.data?.data || null);
        setCurrentView(VIEW_DETAIL);
      }
    } catch (err) {
      message.error("Failed to create and publish tournament");
    } finally {
      setPublishing(false);
    }
  };

  const handleUpdate = async (payload) => {
    if (!selectedTournament?._id) return;
    setSaving(true);
    try {
      const res = await dispatch(
        updateTournament(selectedTournament._id, payload)
      );
      if (res.status) {
        message.success("Tournament updated successfully");
        setSelectedTournament(res.data?.data || null);
        setCurrentView(VIEW_DETAIL);
      } else {
        message.error(res.message || "Failed to update tournament");
      }
    } catch (err) {
      message.error("Failed to update tournament");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateAndPublish = async (payload) => {
    if (!selectedTournament?._id) return;
    setPublishing(true);
    try {
      const updateRes = await dispatch(
        updateTournament(selectedTournament._id, payload)
      );
      if (!updateRes.status) {
        message.error(updateRes.message || "Failed to update tournament");
        return;
      }
      const pubRes = await dispatch(publishTournament(selectedTournament._id));
      if (pubRes.status) {
        message.success("Tournament updated and published successfully");
        setSelectedTournament(null);
        setCurrentView(VIEW_LIST);
      } else {
        message.warning(
          "Updated but failed to publish: " + (pubRes.message || "")
        );
        setSelectedTournament(updateRes.data?.data || selectedTournament);
        setCurrentView(VIEW_DETAIL);
      }
    } catch (err) {
      message.error("Failed to update and publish tournament");
    } finally {
      setPublishing(false);
    }
  };

  const handleRegenerate = async () => {
    if (!selectedTournament?._id) return;
    setRegenerating(true);
    try {
      const res = await dispatch(
        regenerateTournamentQuestions(selectedTournament._id)
      );
      if (res.status) {
        message.success("Questions regenerated successfully");
        setSelectedTournament(res.data?.data || null);
      } else {
        message.error(res.message || "Failed to regenerate questions");
      }
    } catch (err) {
      message.error("Failed to regenerate questions");
    } finally {
      setRegenerating(false);
    }
  };

  const handleRegenerateQuestion = async (questionId) => {
    if (!selectedTournament?._id || !questionId) return;
    setRegeneratingQuestionId(questionId);
    try {
      const res = await dispatch(
        regenerateTournamentQuestion(selectedTournament._id, questionId)
      );
      if (res.status) {
        message.success("Question regenerated successfully");
        setSelectedTournament(res.data?.data || null);
      } else {
        message.error(res.message || "Failed to regenerate question");
      }
    } catch (err) {
      message.error("Failed to regenerate question");
    } finally {
      setRegeneratingQuestionId(null);
    }
  };

  const handleAddAiQuestion = async () => {
    if (!selectedTournament?._id) return;
    setAddingAiQuestion(true);
    try {
      const res = await dispatch(
        generateAdditionalQuestion(selectedTournament._id)
      );
      if (res.status) {
        message.success("AI question added");
        setSelectedTournament(res.data?.data || null);
      } else {
        message.error(res.message || "Failed to add AI question");
      }
    } catch (err) {
      message.error("Failed to add AI question");
    } finally {
      setAddingAiQuestion(false);
    }
  };

  const handlePublish = async () => {
    if (!publishData?._id) return;
    showLoader();
    try {
      const res = await dispatch(publishTournament(publishData._id));
      if (res.status) {
        message.success("Tournament published successfully");
        setPublishModal(false);
        setPublishData(null);
        setSelectedTournament(null);
        setCurrentView(VIEW_LIST);
        fetchTournaments();
      } else {
        message.error(res.message || "Failed to publish tournament");
      }
    } catch (err) {
      message.error("Failed to publish tournament");
    } finally {
      hideLoader();
    }
  };

  const handleUnpublish = async () => {
    const id = unpublishData?._id;
    if (!id) return;
    setUnpublishing(true);
    showLoader();
    try {
      const res = await dispatch(unpublishTournament(id));
      if (res.status) {
        message.success("Tournament moved to draft");
        setUnpublishModal(false);
        setUnpublishData(null);
        setSelectedTournament(res.data?.data || null);
        if (currentView === VIEW_LIST) {
          setSelectedTournament(null);
          fetchTournaments();
        }
      } else {
        message.error(res.message || "Failed to move tournament to draft");
      }
    } catch (err) {
      message.error("Failed to move tournament to draft");
    } finally {
      setUnpublishing(false);
      hideLoader();
    }
  };

  const handleDelete = async () => {
    if (!deleteData?._id) return;
    showLoader();
    try {
      const res = await dispatch(deleteTournament(deleteData._id));
      if (res.status) {
        message.success("Tournament deleted successfully");
        setDeleteModal(false);
        setDeleteData(null);
        fetchTournaments();
      } else {
        message.error(res.message || "Failed to delete tournament");
      }
    } catch (err) {
      message.error("Failed to delete tournament");
    } finally {
      hideLoader();
    }
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      sorter: true,
      onHeaderCell: () => ({ onClick: () => handleSort("title") }),
      render: (text) => <CutOffText maxLength={40} value={text} />,
    },
    {
      title: "Status",
      dataIndex: "lifecycle_status",
      key: "lifecycle_status",
      width: 130,
      render: (status) => (
        <Tag color={lifecycleColor(status)}>{formatStatus(status)}</Tag>
      ),
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
      sorter: true,
      onHeaderCell: () => ({ onClick: () => handleSort("start_date") }),
      width: 210,
      render: (date) => dayjs(date).format(DATETIME_FORMAT),
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
      sorter: true,
      onHeaderCell: () => ({ onClick: () => handleSort("end_date") }),
      width: 230,
      render: (date) => {
        const isExpired = dayjs(date).isBefore(dayjs());
        return (
          <span className={isExpired ? "text-red-500" : ""}>
            {dayjs(date).format(DATETIME_FORMAT)}
            {isExpired && " (Expired)"}
          </span>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 280,
      render: (_, record) => (
        <Space wrap>
          <TableAction
            view={true}
            onView={async () => {
              showLoader();
              try {
                const data = await loadTournament(record._id);
                if (data) setCurrentView(VIEW_DETAIL);
              } finally {
                hideLoader();
              }
            }}
            edit={record.status === "draft"}
            onEdit={async () => {
              if (record.status !== "draft") return;
              showLoader();
              try {
                const data = await loadTournament(record._id);
                if (data) setCurrentView(VIEW_EDIT);
              } finally {
                hideLoader();
              }
            }}
            deleteU={true}
            onDelete={() => {
              setDeleteData(record);
              setDeleteModal(true);
            }}
            traceData={true}
          />
          {record.status === "draft" && (
            <CommonButton
              type="primary"
              onClick={() => {
                setPublishData(record);
                setPublishModal(true);
              }}
              className="rounded-md text-xs bg-[#4f46e5] hover:bg-[#4338ca]"
              size="small"
            >
              Publish
            </CommonButton>
          )}
          {record.status === "published" && (
            <CommonButton
              onClick={() => {
                setUnpublishData(record);
                setUnpublishModal(true);
              }}
              className="rounded-md text-xs"
              size="small"
            >
              Move to Draft
            </CommonButton>
          )}
          {record.lifecycle_status === "completed" &&
            !record.has_correct_answers && (
              <CommonButton
                type="primary"
                onClick={async () => {
                  showLoader();
                  try {
                    const data = await loadTournament(record._id);
                    if (data) setCurrentView(VIEW_DETAIL);
                  } finally {
                    hideLoader();
                  }
                }}
                className="rounded-md text-xs bg-[#16a34a] hover:bg-[#15803d]"
                size="small"
              >
                Add Correct Answers
              </CommonButton>
            )}
        </Space>
      ),
    },
  ];

  const handlePageChange = (page) => setCurrentPage(page);
  const handleSizeChange = (_, size) => setLimit(size);

  const renderContent = () => {
    switch (currentView) {
      case VIEW_CREATE:
        return (
          <TournamentGenerateForm
            onSubmit={handleCreate}
            onCancel={() => setCurrentView(VIEW_LIST)}
            onSwitchToManual={() => setCurrentView(VIEW_CREATE_MANUAL)}
            loading={saving}
          />
        );
      case VIEW_CREATE_MANUAL:
        return (
          <TournamentEditForm
            isCreate
            onSubmit={handleCreate}
            onCancel={() => setCurrentView(VIEW_LIST)}
            onPublish={handleCreateAndPublish}
            loading={saving}
            publishing={publishing}
          />
        );
      case VIEW_EDIT:
        return (
          <TournamentEditForm
            initialData={selectedTournament}
            onSubmit={handleUpdate}
            onCancel={() => {
              setCurrentView(VIEW_LIST);
              setSelectedTournament(null);
            }}
            onPublish={
              selectedTournament?.status === "draft"
                ? handleUpdateAndPublish
                : undefined
            }
            onUnpublish={
              selectedTournament?.status === "published"
                ? () => {
                    setUnpublishData(selectedTournament);
                    setUnpublishModal(true);
                  }
                : undefined
            }
            unpublishing={unpublishing}
            onRegenerate={handleRegenerate}
            onRegenerateQuestion={handleRegenerateQuestion}
            onAddAiQuestion={handleAddAiQuestion}
            loading={saving}
            publishing={publishing}
            regenerating={regenerating}
            addingAiQuestion={addingAiQuestion}
            regeneratingQuestionId={regeneratingQuestionId}
          />
        );
      case VIEW_DETAIL:
        return (
          <TournamentDetails
            key={`${selectedTournament?._id}-${selectedTournament?.status}`}
            tournamentId={selectedTournament?._id}
            onBack={() => {
              setCurrentView(VIEW_LIST);
              setSelectedTournament(null);
            }}
            onEdit={
              selectedTournament?.status === "draft"
                ? () => setCurrentView(VIEW_EDIT)
                : undefined
            }
            onUnpublish={
              selectedTournament?.status === "published"
                ? () => {
                    setUnpublishData(selectedTournament);
                    setUnpublishModal(true);
                  }
                : undefined
            }
            unpublishing={unpublishing}
          />
        );
      default:
        return (
          <>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold font-poppins m-0">
                Tournaments
              </h2>
              <div className="flex gap-2">
                <CommonButton
                  onClick={() => setCurrentView(VIEW_CREATE_MANUAL)}
                  className="rounded-md"
                >
                  + Create Manually
                </CommonButton>
                <CommonButton
                  type="primary"
                  onClick={() => setCurrentView(VIEW_CREATE)}
                  className="rounded-md bg-[#4f46e5] hover:bg-[#4338ca]"
                >
                  + Create with AI
                </CommonButton>
              </div>
            </div>
            <CommonTable
              apiData={tournaments}
              columns={columns}
              currentPage={currentPage}
              itemsPerPage={limit}
              onPageChange={handlePageChange}
              onShowSizeChange={handleSizeChange}
              searchInput={searchInput}
              setSearchInput={setSearchInput}
              onSearchChange={handleSearchChange}
              totalItems={totalItems}
              filter={true}
              showCSV={false}
              additionalFilters={[
                {
                  defaultValue: statusFilter,
                  value: statusFilter,
                  placeholder: "Filter by Status",
                  onChange: (value) => {
                    setStatusFilter(value);
                    setCurrentPage(1);
                  },
                  options: statusFilterOptions,
                },
              ]}
            />

            {deleteModal && (
              <ActionsModal
                title=""
                type="delete"
                isOpen={deleteModal}
                onCancel={() => setDeleteModal(false)}
                onOk={handleDelete}
                footer={false}
                centered={true}
                closable={false}
                maskClosable={false}
                className="delete-modal"
                cancelBtnClass="cancleBtnAction"
                cancelBtnClick={() => {
                  setDeleteModal(false);
                  setDeleteData(null);
                }}
                cancelButtonProps="Cancel"
                saveBtnClass="saveBtnAction"
                saveBtnClick={handleDelete}
                saveButtonProps="Delete"
                description="Do you really want to delete this tournament? This action cannot be undone."
                mainTitle="Are you sure?"
              />
            )}

            {publishModal && (
              <ActionsModal
                title=""
                type="save"
                isOpen={publishModal}
                onCancel={() => setPublishModal(false)}
                onOk={handlePublish}
                footer={false}
                centered={true}
                closable={false}
                maskClosable={false}
                className="delete-modal"
                cancelBtnClass="cancleBtnAction !text-gray-700 !border-gray-300 !bg-gray-200 hover:!bg-gray-300"
                cancelBtnClick={() => {
                  setPublishModal(false);
                  setPublishData(null);
                }}
                cancelButtonProps="Cancel"
                saveBtnClass="saveBtnAction !bg-[#16a34a] hover:!bg-[#15803d] !border-[#16a34a]"
                saveBtnClick={handlePublish}
                saveButtonProps="Publish"
                savebtnType={false}
                description="This tournament will be visible to users. You can still edit or delete it later."
                mainTitle="Publish Tournament?"
              />
            )}
          </>
        );
    }
  };

  return (
    <CustomLayout>
      {renderContent()}
      {unpublishModal && (
        <ActionsModal
          title=""
          type="save"
          isOpen={unpublishModal}
          onCancel={() => {
            setUnpublishModal(false);
            setUnpublishData(null);
          }}
          onOk={handleUnpublish}
          footer={false}
          centered={true}
          closable={false}
          maskClosable={false}
          className="delete-modal"
          cancelBtnClass="cancleBtnAction !bg-gray-200 !text-gray-700 hover:!bg-gray-300 !border-gray-300"
          cancelBtnClick={() => {
            setUnpublishModal(false);
            setUnpublishData(null);
          }}
          cancelButtonProps="Cancel"
          saveBtnClass="saveBtnAction"
          saveBtnClick={handleUnpublish}
          saveButtonProps="Move to Draft"
          savebtnType={false}
          description="This tournament will be hidden from users until you publish it again."
          mainTitle="Move Tournament to Draft?"
        />
      )}
    </CustomLayout>
  );
};

export default withAuth(Tournaments);
